import { cva } from 'class-variance-authority';

const itemVariants = cva('grid w-full grid-cols-[auto_1fr]', {
  variants: {
    selectionListItem: {
      default:
        'my-2 h-8 cursor-pointer items-center gap-2 rounded-sm border border-ui-tertiary bg-ui-primary fill-icon-primary px-2 text-text-primary hover:bg-background-secondary disabled:pointer-events-none disabled:bg-action-disabledBackground disabled:text-text-disabled has-data-[state=checked]:border-tertiary-light has-data-[state=checked]:bg-background-tertiary has-data-[state=checked]:fill-tertiary-main has-data-[state=checked]:text-tertiary-main has-data-[state=checked]:hover:bg-ui-highlight',
      selected:
        'my-2 h-8 items-center gap-2 rounded-sm border border-tertiary-light bg-background-tertiary fill-tertiary-dark px-2 text-tertiary-dark hover:bg-ui-highlight',
    },
    popoverItem: {
      default:
        'h-10 cursor-pointer items-center justify-items-start gap-2 bg-ui-primary fill-icon-primary px-3 text-[14px] leading-4 font-normal text-text-primary hover:bg-ui-hover disabled:pointer-events-none disabled:fill-action-disabled01 disabled:text-text-disabled has-data-[state=checked]:border-tertiary-light has-data-[state=checked]:bg-background-tertiary has-data-[state=checked]:fill-tertiary-dark has-data-[state=checked]:text-tertiary-dark has-data-[state=checked]:hover:bg-ui-highlight',
      selected:
        'h-10 items-center justify-items-start gap-2 bg-background-tertiary fill-tertiary-dark px-3 text-[14px] leading-4 font-normal text-tertiary-dark hover:bg-ui-highlight disabled:pointer-events-none disabled:bg-ui-primary disabled:fill-action-disabled01 disabled:text-text-disabled',
    },
    cardItem: {
      default:
        'pointer-events-auto h-fit w-fit cursor-pointer flex-nowrap rounded-md bg-ui-primary p-3 shadow-[0px_0px_5px_0px_rgba(23,24,24,0.05),0px_1px_2px_0px_rgba(0,0,0,0.15)] hover:bg-ui-secondary hover:shadow-[0px_2px_4px_-2px_rgba(16,24,40,0.06),0px_4px_8px_-2px_rgba(16,24,40,0.10)] aria-disabled:pointer-events-none aria-disabled:border-none aria-disabled:bg-ui-secondary aria-disabled:shadow-none',
    },
    draggableItem: {
      default:
        'm-0 flex w-full min-w-20 items-center gap-2 rounded-sm border border-ui-tertiary bg-ui-primary fill-icon-primary px-2 text-text-primary hover:bg-ui-secondary disabled:pointer-events-none disabled:bg-action-disabledBackground disabled:text-text-disabled data-[size=medium]:h-12 data-[size=small]:h-8',
    },
  },
});

const inputVariants = cva(
  'group/input inline-flex w-full justify-center rounded-sm bg-ui-field02 px-3 text-[14px] text-text-primary placeholder:text-text-placeholder read-only:bg-ui-field01 read-only:text-text-secondary read-only:hover:border-border-primary read-only:focus-visible:border read-only:focus-visible:border-border-primary read-only:focus-visible:outline-none disabled:border-border-disabled disabled:bg-action-disabledBackground disabled:text-text-disabled02 disabled:placeholder:text-action-disabled01 disabled:focus-visible:border disabled:focus-visible:outline-none',

  {
    variants: {
      size: {
        medium: 'h-10',
        small: 'h-8',
      },
      color: {
        default:
          'border border-border-primary hover:border-action-focusBorder focus-visible:border-transparent-main focus-visible:outline-2 focus-visible:outline-action-focusBorder',
        warning:
          'border-2 border-warning-main focus-visible:border-warning-main focus-visible:outline-none disabled:border',
        error:
          'border-2 border-error-main focus-visible:border-error-main focus-visible:outline-none disabled:border',
      },
    },
    defaultVariants: {
      size: 'medium',
      color: 'default',
    },
  },
);
const buttonVariants = cva(
  'flex w-fit shrink-0 cursor-pointer items-center justify-center rounded-sm text-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-action-focusBorder disabled:pointer-events-none disabled:fill-text-disabled disabled:text-text-disabled',
  {
    variants: {
      color: {
        primary:
          'bg-primary-main fill-primary-contrastText text-primary-contrastText hover:bg-primary-shade active:bg-primary-active-shade',
        //secondary color of contained variant is deprecated. so as a temporary solution we added class names with hex colors.
        secondary:
          'bg-[#49545c] fill-[#ffffff] text-[#ffffff] hover:bg-[#68737d] focus-visible:outline-[#72b1fc] active:bg-[#87929d] dark:bg-[#C7D1DB] dark:fill-[#161A1D] dark:text-[#161A1D] dark:hover:bg-[#B6C2CF] dark:focus-visible:outline-[#BDDFFF] dark:active:bg-[#9FADBC]',
        warning:
          'bg-warning-main fill-warning-contrastText text-warning-contrastText hover:bg-warning-hover active:bg-warning-active',
        error:
          'bg-error-main fill-error-contrastText text-error-contrastText hover:bg-error-hover active:bg-error-active',
        ai: 'bg-linear-to-bl from-ai-lightGreen to-ai-lightCyan fill-primary-contrastText text-primary-contrastText hover:from-ai-hoverCyan hover:to-ai-hoverGreen focus:outline-background-teal active:from-ai-activeCyan active:to-ai-activeGreen disabled:bg-none',
      },
      icon: {
        false: null,
      },
      variant: {
        contained: 'disabled:bg-action-disabledBackground',
        outlined:
          'border border-secondary-main bg-transparent-main fill-secondary-dark text-secondary-dark hover:bg-secondary-hover focus-visible:outline-action-focusBorder active:bg-secondary-active disabled:border-ui-tertiary disabled:bg-transparent-main',
        text: 'bg-transparent-main fill-tertiary-main text-tertiary-main hover:bg-tertiary-hover focus-visible:outline-action-focusBorder active:bg-tertiary-active disabled:bg-transparent-main',
        plain:
          'gap-1 bg-transparent-main fill-tertiary-main px-0 text-[14px] leading-4 text-tertiary-main hover:bg-transparent-main hover:fill-tertiary-dark hover:text-tertiary-dark focus-visible:outline-action-focusBorder active:bg-transparent-main active:fill-tertiary-dark active:text-tertiary-dark',
      },
      size: {
        xLargeMobile:
          'h-14 gap-2 p-4 text-[18px] leading-5.5 font-medium [&>svg]:size-6',
        largeMobile:
          'h-12 gap-2 p-4 text-[18px] leading-5.5 font-medium [&>svg]:size-6',
        large:
          'h-10 gap-2 px-4 py-3 text-[14px] leading-4 font-medium [&>svg]:size-4',
        medium:
          'h-8 gap-2 px-3 py-2 text-[14px] leading-4 font-medium [&>svg]:size-4',
        small:
          'h-6 gap-1 px-2 py-1 text-[12px] leading-4 font-medium [&>svg]:size-4',
      },
    },
    compoundVariants: [
      {
        icon: true,
        variant: ['text', 'outlined', 'contained'],
        size: 'small',
        class: 'w-6 p-1',
      },
      {
        icon: true,
        variant: ['text', 'outlined', 'contained'],
        size: 'medium',
        class: 'w-8 p-2',
      },
      {
        icon: true,
        variant: ['text', 'outlined', 'contained'],
        size: 'large',
        class: 'w-10 p-3',
      },
      {
        icon: true,
        variant: ['text', 'outlined', 'contained'],
        size: 'largeMobile',
        class: 'w-12 p-3 *:size-6',
      },
      {
        icon: true,
        variant: ['text', 'outlined', 'contained'],
        size: 'xLargeMobile',
        class: 'w-14 *:size-6',
      },
      {
        color: 'error',
        variant: 'text',
        class:
          'fill-error-dark text-error-dark hover:bg-error-main/8 focus-visible:outline-error-light active:bg-error-main/16',
      },
      {
        color: 'warning',
        variant: 'text',
        class:
          'fill-warning-dark text-warning-dark hover:bg-warning-main/8 focus-visible:outline-warning-light active:bg-warning-main/16 active:outline-none',
      },
      {
        variant: 'plain',
        size: ['xLargeMobile', 'largeMobile', 'large', 'medium', 'small'],
        class: 'h-4 p-0 text-[14px] leading-4',
      },
      {
        variant: 'plain',
        size: ['xLargeMobile', 'largeMobile', 'large', 'medium', 'small'],
        icon: true,
        class: 'w-4',
      },
      {
        color: 'error',
        variant: 'outlined',
        class:
          'border-error-main fill-error-dark text-error-dark hover:bg-error-main/8 focus-visible:outline-error-light active:bg-error-main/16',
      },
      {
        color: 'warning',
        variant: 'outlined',
        class:
          'border-warning-main fill-warning-dark text-warning-dark hover:bg-warning-main/8 focus:outline-warning-light active:bg-warning-main/16 active:outline-none',
      },
    ],
    defaultVariants: {
      color: 'primary',
      variant: 'contained',
      size: 'medium',
      icon: false,
    },
  },
);
const dialogVariants = cva(
  'group/dialog fixed top-[50%] left-[50%] z-dialog flex w-full translate-x-[-50%] translate-y-[-50%] flex-col rounded-[3px] bg-ui-primary shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
  {
    variants: {
      // The max heights match the MUI Dialog sizes; DialogContent scrolls
      // when the content is taller.
      size: {
        xs: 'max-h-[48vh] max-w-90',
        s: 'max-h-[72vh] max-w-90 md:max-w-130',
        m: 'max-h-[84vh] max-w-170',
        lg: 'max-h-[96vh] max-w-260',
        full: 'h-full max-w-full rounded-none',
      },
    },
  },
);

const iconVariants = cva('shrink-0', {
  variants: {
    size: {
      inherit: 'h-4 w-4',
      small: 'h-4 w-4',
      medium: 'h-4 w-4',
      large: 'h-5 w-5',
    },
    color: {
      inherit: 'fill-current',
      action: 'fill-icon-primary',
      disabled: 'fill-action-disabled01',
      primary: 'fill-primary-main',
      secondary: 'fill-secondary-main',
      error: 'fill-error-main',
      info: 'fill-info-main',
      success: 'fill-success-main',
      warning: 'fill-warning-main',
      purple: 'fill-purple-main',
      teal: 'fill-teal-main',
      text: 'fill-text-primary',
      tertiary: 'fill-tertiary-main',
      yellow: 'fill-yellow-main',
      dark: 'fill-dark-main',
    },
  },
});

const toggleVariants = cva(
  'flex w-fit items-center justify-center gap-2 rounded-sm border border-ui-tertiary bg-ui-primary fill-text-secondary font-medium text-nowrap text-text-secondary hover:bg-ui-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-action-focusBorder data-disabled:border-none data-disabled:bg-transparent-main data-disabled:fill-action-disabled01 data-disabled:text-action-disabled01 data-[state=on]:border-tertiary-light data-[state=on]:bg-background-tertiary data-[state=on]:fill-tertiary-dark data-[state=on]:text-tertiary-dark hover:data-[state=on]:bg-ui-highlight',
  {
    variants: {
      size: {
        large: 'h-10 px-4 py-3 text-[14px] leading-4',
        medium: 'h-8 px-4 py-2 text-[14px] leading-4',
        small: 'h-6 px-4 py-1 text-[14px] leading-4',
      },
      icon: {
        false: null,
      },
    },
    compoundVariants: [
      {
        size: 'large',
        icon: true,
        className: 'p-3',
      },
      {
        size: 'medium',
        icon: true,
        className: 'p-2',
      },
      {
        size: 'small',
        icon: true,
        className: 'p-1',
      },
    ],
    defaultVariants: {
      size: 'medium',
      icon: false,
    },
  },
);

export {
  itemVariants,
  inputVariants,
  buttonVariants,
  dialogVariants,
  iconVariants,
  toggleVariants,
};
